import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPinDetails,
  fetchUserDetails,
  fetchAccessCodeDetails,
  searchAndValidatePractice,
  movePracticeUser,
  updateUserAccess,
  inviteUser,
  updateUserType,
  resendInvite,
} from "./manageUserActions";
import { getApiErrorMessage } from "../../utils/commonUtilities";

const pinDetailsSlice = createSlice({
  name: "pinDetails",
  initialState: {
    isLoadingPinDetails: false,
    errorPinDetails: null,
    errorPinDetailsMsg: "",
    pinDetails: [],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPinDetails.pending, (state) => {
        state.isLoadingPinDetails = true;
        state.errorPinDetails = null;
        state.errorPinDetailsMsg = "";
      })
      .addCase(fetchPinDetails.fulfilled, (state, action) => {
        state.isLoadingPinDetails = false;
        if (action.payload?.data?.practicePinResponseDTO?.pin) {
          state.pinDetails = [action.payload.data.practicePinResponseDTO.pin];
        } else {
          state.pinDetails = action.payload;
        }
      })
      .addCase(fetchPinDetails.rejected, (state, action) => {
        state.isLoadingPinDetails = false;
        state.errorPinDetails = true;
        if (action.payload) {
          state.errorPinDetailsMsg = getApiErrorMessage(action.payload.status);
        } else {
          state.errorPinDetailsMsg = action.error.message;
        }
      });
  },
});

const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState: {
    isLoadingUserDetails: false,
    errorUserDetails: null,
    errorUserDetailsMsg: "",
    userDetails: [],
  },
  reducers: {
    addUserData: (state, action) => {
      return {
        ...state,
        userDetails: [...state.userDetails, action.payload],
      };
    },
    updateUserData: (state, action) => {
      return {
        ...state,
        userDetails: state.userDetails.map((user) =>
          user.userId === action.payload.userId
            ? { ...user, ...action.payload }
            : user
        ),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.isLoadingUserDetails = true;
        state.errorUserDetails = null;
        state.errorUserDetailsMsg = "";
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.isLoadingUserDetails = false;
        if (action.payload?.data?.practiceUserDTO) {
          state.userDetails = action.payload.data.practiceUserDTO;
        } else {
          state.userDetails = action.payload;
        }
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.isLoadingUserDetails = false;
        state.errorUserDetails = true;
        if (action.payload) {
          state.errorUserDetailsMsg = getApiErrorMessage(action.payload.status);
        } else {
          state.errorUserDetailsMsg = action.error.message;
        }
      });
  },
});

const userAccessSlice = createSlice({
  name: "userAccess",
  initialState: {
    isLoadingUserAccess: false,
    errorUserAccess: null,
    errorUserAccessMsg: "",
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateUserAccess.pending, (state) => {
        state.isLoadingUserAccess = true;
        state.errorUserAccess = null;
        state.errorUserAccessMsg = "";
      })
      .addCase(updateUserAccess.fulfilled, (state) => {
        state.isLoadingUserAccess = false;
      })
      .addCase(updateUserAccess.rejected, (state, action) => {
        state.isLoadingUserAccess = false;
        state.errorUserAccess = true;
        if (action.payload) {
          state.errorUserAccessMsg = getApiErrorMessage(action.payload.status);
        } else {
          state.errorUserAccessMsg = action.error.message;
        }
      });
  },
});

const accessCodeDetailsSlice = createSlice({
  name: "accessCodeDetails",
  initialState: {
    isLoadingAccessCodeDetails: false,
    errorAccessCodeDetails: null,
    errorAccessCodeDetailsMsg: "",
    accessCodeDetails: [],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccessCodeDetails.pending, (state) => {
        state.isLoadingAccessCodeDetails = true;
        state.errorAccessCodeDetails = null;
        state.errorAccessCodeDetailsMsg = "";
      })
      .addCase(fetchAccessCodeDetails.fulfilled, (state, action) => {
        state.isLoadingAccessCodeDetails = false;
        if (action.payload?.data?.practiceExtendedResponseDTO) {
          state.accessCodeDetails = [
            {
              accessCode:
                action.payload.data.practiceExtendedResponseDTO.accessCode,
            },
          ];
        } else {
          state.accessCodeDetails = action.payload;
        }
      })
      .addCase(fetchAccessCodeDetails.rejected, (state, action) => {
        state.isLoadingAccessCodeDetails = false;
        state.errorAccessCodeDetails = true;
        if (action.payload) {
          state.errorAccessCodeDetailsMsg = getApiErrorMessage(
            action.payload.status
          );
        } else {
          state.errorAccessCodeDetailsMsg = action.error.message;
        }
      });
  },
});

const searchPracticeDetailsSlice = createSlice({
  name: "searchPracticeDetails",
  initialState: {
    isLoadingSearchPractice: false,
    errorSearchPractice: null,
    errorSearchPracticeMsg: "",
    searchPracticeDetails: null,
  },
  reducers: {
    clearSearchPracticeDetails: (state) => {
      state.searchPracticeDetails = null;
      state.errorSearchPractice = null;
      state.errorSearchPracticeMsg = "";
      state.isLoadingSearchPractice = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchAndValidatePractice.pending, (state) => {
        state.isLoadingSearchPractice = true;
        state.errorSearchPractice = null;
        state.errorSearchPracticeMsg = "";
        state.searchPracticeDetails = null;
      })
      .addCase(searchAndValidatePractice.fulfilled, (state, action) => {
        state.isLoadingSearchPractice = false;
        if (action.payload?.data?.practiceResponseDTO?.items) {
          state.searchPracticeDetails =
            action.payload.data.practiceResponseDTO.items[0];
        } else {
          state.searchPracticeDetails = action.payload;
        }
      })
      .addCase(searchAndValidatePractice.rejected, (state, action) => {
        state.isLoadingSearchPractice = false;
        state.errorSearchPractice = true;
        if (action.payload?.message) {
          state.errorSearchPracticeMsg = action.payload.message;
        } else if (action.payload?.status) {
          state.errorSearchPracticeMsg = getApiErrorMessage(
            action.payload.status
          );
        } else {
          state.errorSearchPracticeMsg = action.error.message;
        }
        state.searchPracticeDetails = null;
      });
  },
});

const movePracticeDetailsSlice = createSlice({
  name: "movePracticeDetails",
  initialState: {
    isLoadingMovePractice: false,
    errorMovePractice: null,
    errorMovePracticeMsg: "",
    movePracticeDetails: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(movePracticeUser.pending, (state) => {
        state.isLoadingMovePractice = true;
        state.errorMovePractice = null;
        state.errorMovePracticeMsg = "";
      })
      .addCase(movePracticeUser.fulfilled, (state, action) => {
        state.isLoadingMovePractice = false;
        state.movePracticeDetails = action.payload;
      })
      .addCase(movePracticeUser.rejected, (state, action) => {
        state.isLoadingMovePractice = false;
        state.errorMovePractice = true;
        if (action.payload) {
          state.errorMovePracticeMsg = getApiErrorMessage(
            action.payload.status
          );
        } else {
          state.errorMovePracticeMsg = action.error.message;
        }
      });
  },
});

const inviteUserSlice = createSlice({
  name: "inviteUser",
  initialState: {
    isLoadingInviteUser: false,
    errorInviteUser: null,
    errorInviteUserMsg: "",
    inviteUserDetails: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(inviteUser.pending, (state) => {
        state.isLoadingInviteUser = true;
        state.errorInviteUser = null;
        state.errorInviteUserMsg = "";
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.isLoadingInviteUser = false;
        state.errorInviteUser = false;
        state.errorInviteUserMsg = "";
        state.inviteUserDetails = action.payload;
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.isLoadingInviteUser = false;
        state.errorInviteUser = true;
        if (action.payload) {
          state.errorInviteUserMsg = getApiErrorMessage(action.payload.status);
        } else {
          state.errorInviteUserMsg = action.error.message;
        }
      });
  },
});

const updateUserTypeSlice = createSlice({
  name: "updateUserType",
  initialState: {
    isLoadingUpdateUserType: false,
    errorUpdateUserType: null,
    errorUpdateUserTypeMsg: "",
    updateUserTypeDetails: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateUserType.pending, (state) => {
        state.isLoadingUpdateUserType = true;
        state.errorUpdateUserType = null;
        state.errorUpdateUserTypeMsg = "";
      })
      .addCase(updateUserType.fulfilled, (state, action) => {
        state.isLoadingUpdateUserType = false;
        state.errorUpdateUserType = false;
        state.errorUpdateUserTypeMsg = "";
        state.updateUserTypeDetails = action.payload;
      })
      .addCase(updateUserType.rejected, (state, action) => {
        state.isLoadingUpdateUserType = false;
        state.errorUpdateUserType = true;
        if (action.payload) {
          state.errorUpdateUserTypeMsg = getApiErrorMessage(
            action.payload.status
          );
        } else {
          state.errorUpdateUserTypeMsg = action.error.message;
        }
      });
  },
});

const resendInviteSlice = createSlice({
  name: "resendInvite",
  initialState: {
    isLoadingResendInvite: false,
    errorResendInvite: null,
    errorResendInviteMsg: "",
    resendInviteDetails: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resendInvite.pending, (state) => {
        state.isLoadingResendInvite = true;
        state.errorResendInvite = null;
        state.errorResendInviteMsg = "";
      })
      .addCase(resendInvite.fulfilled, (state, action) => {
        state.isLoadingResendInvite = false;
        if (!action.payload) {
          state.errorResendInvite = true;
          state.errorResendInviteMsg = getApiErrorMessage(
            action.payload.status
          );
        } else {
          state.resendInviteDetails = action.payload;
        }
      })
      .addCase(resendInvite.rejected, (state, action) => {
        state.isLoadingResendInvite = false;
        state.errorResendInvite = true;
        if (action.payload) {
          state.errorResendInviteMsg = getApiErrorMessage(
            action.payload.status
          );
        } else {
          state.errorResendInviteMsg =
            "Something Went Wrong!. Please try again later.";
        }
      });
  },
});

export const { addUserData, updateUserData } = userDetailsSlice.actions;
export const { clearSearchPracticeDetails } =
  searchPracticeDetailsSlice.actions;
export const pinDetailsReducer = pinDetailsSlice.reducer;
export const userDetailsReducer = userDetailsSlice.reducer;
export const userAccessReducer = userAccessSlice.reducer;
export const inviteUserReducer = inviteUserSlice.reducer;
export const updateUserTypeReducer = updateUserTypeSlice.reducer;
export const accessCodeDetailsReducer = accessCodeDetailsSlice.reducer;
export const searchPracticeDetailsReducer = searchPracticeDetailsSlice.reducer;
export const movePracticeDetailsReducer = movePracticeDetailsSlice.reducer;
export const resendInviteReducer = resendInviteSlice.reducer;
